import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  MaxsoldAuctionDto,
  MaxsoldAuctionSearchDto,
  MaxsoldSaleSort,
  MaxsoldSaleState,
  MaxsoldSearchConfigDto,
  MaxsoldSearchView,
} from '@jordan/types';
import { Alert, Breadcrumb, Button, DataTable, PageHeader, Select, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';
import { maxsoldSidebar } from './maxsoldSidebar';
import { MaxsoldPlaceSearch, MaxsoldSearchMap } from './MaxsoldSearchMap';
import { auctionPlace } from './maxsoldSearchUtils';
import { ListingGrid, listingColumns, type ListingView } from './MaxsoldListing';

/** The server-side filters — any change re-runs the search from page 0. */
interface ServerParams {
  lat: number;
  lng: number;
  radiusKm: number;
  country: string;
  saleState: MaxsoldSaleState;
  sort: MaxsoldSaleSort;
}

const SORT_OPTIONS = [
  { value: 'close_time_asc', label: 'Closing soonest' },
  { value: 'close_time_desc', label: 'Closing latest' },
  { value: 'distance_asc', label: 'Nearest' },
];
const STATE_OPTIONS = [
  { value: 'open', label: 'Open (live)' },
  { value: 'closed', label: 'Closed' },
];
const COUNTRY_OPTIONS = [
  { value: 'canada', label: 'Canada' },
  { value: 'usa', label: 'United States' },
];

const buildQuery = (p: ServerParams, pageNumber: number) =>
  new URLSearchParams({
    lat: String(p.lat),
    lng: String(p.lng),
    radiusKm: String(p.radiusKm),
    country: p.country,
    saleState: p.saleState,
    sort: p.sort,
    pageNumber: String(pageNumber),
  }).toString();

export function MaxsoldSearchPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [cfg, setCfg] = useState<MaxsoldSearchConfigDto | null>(null);
  const [params, setParams] = useState<ServerParams | null>(null);
  const [locLabel, setLocLabel] = useState('');

  const [results, setResults] = useState<MaxsoldAuctionDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [view, setView] = useState<MaxsoldSearchView>('list');
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('');
  // Ticks once a minute so the "time left" countdown stays current without a manual refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Load page config (maps key + defaults + presets), then seed the initial search.
  useEffect(() => {
    apiGet<MaxsoldSearchConfigDto>('/api/maxsold/search/config')
      .then((c) => {
        setCfg(c);
        setParams({
          lat: c.defaultLat,
          lng: c.defaultLng,
          radiusKm: c.defaultRadiusKm,
          country: c.defaultCountry,
          saleState: c.defaultSaleState,
          sort: c.defaultSort,
        });
        const preset = c.locations.find((l) => l.lat === c.defaultLat && l.lng === c.defaultLng);
        setLocLabel(preset?.label ?? c.locations[0]?.label ?? 'your area');
      })
      .catch(() => setErr('Failed to load search settings.'));
  }, []);

  // Any change to the server params re-runs the search from page 0.
  useEffect(() => {
    if (!params) return;
    setLoading(true);
    setErr(null);
    setCategory('');
    apiGet<MaxsoldAuctionSearchDto>(`/api/maxsold/search?${buildQuery(params, 0)}`)
      .then((r) => {
        setResults(r.items);
        setTotal(r.total);
        setPage(0);
      })
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not search auctions.');
        setResults([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [params]);

  const loadMore = useCallback(() => {
    if (!params || loadingMore) return;
    const next = page + 1;
    setLoadingMore(true);
    apiGet<MaxsoldAuctionSearchDto>(`/api/maxsold/search?${buildQuery(params, next)}`)
      .then((r) => {
        setResults((prev) => [...prev, ...r.items]);
        setTotal(r.total);
        setPage(next);
      })
      .catch(() => setErr('Could not load more auctions.'))
      .finally(() => setLoadingMore(false));
  }, [params, page, loadingMore]);

  const patch = (p: Partial<ServerParams>) => setParams((prev) => (prev ? { ...prev, ...p } : prev));

  const openAuction = useCallback((id: number) => navigate(`/maxsold/auction/${id}`), [navigate]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setErr('Geolocation is not available in this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        patch({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocLabel('My location');
      },
      () => setErr('Could not get your location (permission denied?).'),
    );
  };

  const onPresetChange = (idx: string) => {
    const preset = cfg?.locations[Number(idx)];
    if (!preset) return;
    patch({ lat: preset.lat, lng: preset.lng, country: preset.country });
    setLocLabel(preset.label);
  };

  // Client-side keyword + category filtering over the loaded results.
  const kw = keyword.trim().toLowerCase();
  const visible = useMemo(
    () =>
      results.filter(
        (a) =>
          (category === '' || a.category === category) &&
          (kw === '' ||
            `${a.title} ${a.city ?? ''} ${a.province ?? ''} ${a.region ?? ''} ${a.category ?? ''}`
              .toLowerCase()
              .includes(kw)),
      ),
    [results, category, kw],
  );

  const categoryOptions = useMemo(() => {
    const cats = Array.from(new Set(results.map((a) => a.category).filter((c): c is string => !!c))).sort();
    return [{ value: '', label: 'All categories' }, ...cats.map((c) => ({ value: c, label: c }))];
  }, [results]);

  const radiusOptions = (cfg?.radiusOptionsKm ?? [50]).map((km) => ({ value: String(km), label: `${km} km` }));
  const presetOptions = (cfg?.locations ?? []).map((l, i) => ({ value: String(i), label: l.label }));
  const hasMore = results.length < total;

  // Auctions mapped into the shared listing model (no price/heart; category badge + lots + distance).
  const views = useMemo<ListingView[]>(
    () =>
      visible.map((a) => ({
        id: a.id,
        imageUrl: a.imageUrls[0] ?? null,
        title: a.title,
        subtitle: auctionPlace(a),
        badge: a.category ? { label: a.category, variant: 'dark', soft: true } : null,
        lotCount: a.lotCount,
        bidCount: a.bidCount,
        distanceMeters: a.distanceMeters,
        closeTime: a.closeTime,
        externalUrl: a.url,
        mapUrl: a.mapUrl,
      })),
    [visible],
  );

  const columns = useMemo(
    () => listingColumns({ now, titleHeader: 'Auction', lots: true, distance: true, actions: true }),
    [now],
  );

  return (
    <AppChrome secondarySidebar={maxsoldSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Maxsold - <span className="fw-normal">Search</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Maxsold' }, { label: 'Search' }]} />}
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {/* Filters */}
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-lg-5">
                <label className="form-label">Location</label>
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <MaxsoldPlaceSearch
                      apiKey={cfg?.mapsApiKey ?? null}
                      country={params?.country}
                      onPick={({ lat, lng, label }) => {
                        patch({ lat, lng });
                        setLocLabel(label);
                      }}
                    />
                  </div>
                  <Button variant="light" icon="ph-crosshair" onClick={useMyLocation} title="Use my location">
                    Near me
                  </Button>
                </div>
                <div className="form-text">
                  Searching near <span className="fw-semibold">{locLabel}</span>
                </div>
              </div>

              <div className="col-6 col-lg-2">
                <Select
                  label="Preset"
                  options={presetOptions}
                  value={presetOptions.find((o) => o.label === locLabel)?.value ?? ''}
                  onChange={(e) => onPresetChange(e.target.value)}
                />
              </div>

              <div className="col-6 col-lg-2">
                <Select
                  label="Within"
                  options={radiusOptions}
                  value={String(params?.radiusKm ?? '')}
                  onChange={(e) => patch({ radiusKm: Number(e.target.value) })}
                />
              </div>

              <div className="col-6 col-lg-3">
                <Select
                  label="Country"
                  options={COUNTRY_OPTIONS}
                  value={params?.country ?? 'canada'}
                  onChange={(e) => patch({ country: e.target.value })}
                />
              </div>

              <div className="col-6 col-lg-3">
                <Select
                  label="Status"
                  options={STATE_OPTIONS}
                  value={params?.saleState ?? 'open'}
                  onChange={(e) => patch({ saleState: e.target.value as MaxsoldSaleState })}
                />
              </div>

              <div className="col-6 col-lg-3">
                <Select
                  label="Sort"
                  options={SORT_OPTIONS}
                  value={params?.sort ?? 'close_time_asc'}
                  onChange={(e) => patch({ sort: e.target.value as MaxsoldSaleSort })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="card">
          <div className="card-header d-flex flex-wrap align-items-center gap-2">
            <h5 className="mb-0 me-auto">
              Auctions
              {!loading && (
                <span className="text-muted fw-normal ms-2">
                  {visible.length}
                  {total > results.length ? ` of ${total}` : ''} found
                </span>
              )}
            </h5>

            <div className="form-control-feedback form-control-feedback-start" style={{ maxWidth: 220 }}>
              <input
                type="search"
                className="form-control"
                placeholder="Filter results…"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <div className="form-control-feedback-icon">
                <i className="ph-funnel opacity-50" />
              </div>
            </div>

            {/* Raw select (not <Select>) so it has no form-group bottom margin and stays aligned in the header row. */}
            <select
              className="form-select"
              aria-label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ maxWidth: 180, width: 'auto' }}
            >
              {categoryOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <div className="btn-group" role="group" aria-label="View">
              {(['list', 'grid', 'map'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setView(v)}
                  title={v[0].toUpperCase() + v.slice(1)}
                >
                  <i className={v === 'list' ? 'ph-list' : v === 'grid' ? 'ph-squares-four' : 'ph-map-trifold'} />
                </button>
              ))}
            </div>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <Spinner />
              </div>
            ) : view === 'map' ? (
              <MaxsoldSearchMap
                apiKey={cfg?.mapsApiKey ?? null}
                mapsConfigured={cfg?.mapsConfigured ?? false}
                auctions={visible}
                center={{ lat: params?.lat ?? 0, lng: params?.lng ?? 0 }}
                onOpenAuction={openAuction}
              />
            ) : view === 'grid' ? (
              <ListingGrid views={views} now={now} onOpen={openAuction} emptyMessage="No auctions match." />
            ) : (
              <DataTable
                columns={columns}
                rows={views}
                getRowKey={(v) => String(v.id)}
                onRowClick={(v) => openAuction(v.id)}
                initialSort={{ key: params?.sort === 'distance_asc' ? 'distanceMeters' : 'closeTime', dir: 'asc' }}
                emptyMessage="No auctions match."
              />
            )}

            {hasMore && (
              <div className="text-center mt-3">
                <Button variant="light" icon="ph-arrow-down" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? 'Loading…' : `Load more (${results.length} of ${total})`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppChrome>
  );
}
